import { PlanIndexes, tabNames } from '@va/constants';
import { startUpgradeProcess } from '@va/dashboard/actions/app';
import { getInstanceId, isFeedbackAdvertisingFeatureEnabled } from '@va/dashboard/selectors/app';
import { getProviderName } from '@va/dashboard/selectors/customization';
import ToggleButtonsField from '@va/deprecated/form/ToggleButtonsField';
import { useTranslate } from '@va/localization';
import { setUpgradePlanIndex } from '@va/standalone/shared/actions';
import { hasPermission } from '@va/standalone/shared/selectors';
import { Paragraph, paragraphSizes } from '@va/ui/design-system';
import { TooltipWrapper } from '@va/ui/tooltips';
import { useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { FormElement } from './FormElement';

type AdvertisingOptionProps = {
  tab: string;
  disabled?: boolean;
};

export const AdvertisingOption = ({ tab, disabled }: AdvertisingOptionProps) => {
  const translate = useTranslate();
  const dispatch = useDispatch();

  const websiteId = useSelector(getInstanceId);
  const onUpgradeClick = () => dispatch(startUpgradeProcess({ websiteId }));
  const providerName = useSelector(getProviderName);

  const hasUpgradeAccountPlanPermission = useSelector(hasPermission);
  const hasAdvertisingChangePermission = useSelector(isFeedbackAdvertisingFeatureEnabled);
  const isPollsTab = useMemo(() => tab === tabNames.polls, [tab]);

  return (
    <>
      <Paragraph size={paragraphSizes.tiny} colorClassName='text-green' className='relative top-4'>
        {translate('label.premiumFeature')}
      </Paragraph>
      {hasAdvertisingChangePermission ? (
        <FormElement
          input={AdvertisingButtonsField}
          label={
            isPollsTab
              ? translate('feedback.polls.createdWithVisa.label', { providerName })
              : translate('feedback.surveys.poweredByVisa.label', { providerName })
          }
          name={'advertising'}
          disabled={disabled}
        />
      ) : (
        <div className='lg:flex items-center justify-between'>
          <label className='font-bold block py-2 pr-2'>
            {isPollsTab
              ? translate('feedback.polls.removeVisaLink.label', { providerName })
              : translate('feedback.surveys.removeVisaLink.label', { providerName })}
            :
          </label>

          <TooltipWrapper
            interactive
            disabled={hasUpgradeAccountPlanPermission}
            content={translate('tooltips.contributorRole')}
          >
            <div
              className='h-[3.25rem] min-w-240 cursor-pointer flex justify-center items-center text-white font-semibold rounded-24 bg-gradient-to-r from-[#f66f1e] to-[#ffba42]'
              onClick={() => {
                if (!hasUpgradeAccountPlanPermission) return;
                dispatch(setUpgradePlanIndex(PlanIndexes.Pro));
                onUpgradeClick();
              }}
            >
              {translate('actions.upgradeAccount')}
            </div>
          </TooltipWrapper>
        </div>
      )}
    </>
  );
};

type AdvertisingButtonsFieldProps = {
  name: string;
  disabled?: boolean;
};

const AdvertisingButtonsField = ({ name, disabled }: AdvertisingButtonsFieldProps) => {
  const translate = useTranslate();

  const options = useMemo(
    () => [
      { label: translate('option.btn.disabled'), value: false },
      { label: translate('option.btn.enabled'), value: true },
    ],
    [translate],
  );

  return <ToggleButtonsField options={options} className='grow' name={name} disabled={disabled} />;
};
